import { json, redirect, type DataFunctionArgs } from '@remix-run/node'
import { Link, useLoaderData, type MetaFunction } from '@remix-run/react'
import { PaginationBar } from '#app/components/commonElements/PaginationBar.tsx'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { ErrorList } from '#app/components/forms.tsx'
import PageTitle from '#app/components/pageLayout/PageTitle.tsx'
import { SearchBar } from '#app/components/search-bar.tsx'
import { Badge } from '#app/components/ui/badge.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { cn, useDelayedIsPending } from '#app/utils/misc.tsx'

export async function loader({ request }: DataFunctionArgs) {
	const url = new URL(request.url)
	const page = Number(url.searchParams.get('page')) || 1
	const pageSize = 10
	const skip = (page - 1) * pageSize

	const searchTerm = url.searchParams.get('q')
	if (searchTerm === '') {
		return redirect('/buscar')
	}
	const like = `%${searchTerm ?? ''}%`

	const [procedures, totalCount] = await Promise.all([
		prisma.procedure.findMany({
			skip: skip,
			take: pageSize,
			select: {
				id: true,
				name: true,
				slug: true,
				content: true,
				administration: { select: { name: true, slug: true, country: true } },
			},
			where: {
				name: {
					contains: like,
				},
			},
			orderBy: {
				updatedAt: 'desc',
			},
		}),
		prisma.procedure.count({
			where: {
				name: {
					contains: like,
				},
			},
		}),
	])

	return json({ status: 'idle', procedures, totalCount, page, pageSize } as const)
}

export default function UsersRoute() {
	const data = useLoaderData<typeof loader>()
	const isPending = useDelayedIsPending({
		formMethod: 'GET',
		formAction: '/buscar',
	})

	return (
		<div className="pt-12">
			<PageTitle
				title={'Buscar'}
				sizeClasses="text-3xl sm:text-5xl lg:text-5xl"
			/>{' '}
			<div className="prose mb-8">
				<SearchBar status={data.status} autoFocus autoSubmit />
			</div>
			<main>
				{data.status === 'idle' ? (
					data.procedures.length ? (
						<>
							<ul
								className={cn('mt-8 divide-y divide-gray-200', {
									'opacity-50': isPending,
								})}
							>
								{data.procedures.map(procedure => (
									<li
										key={procedure.id}
										className="prose relative bg-white px-4 py-5 focus-within:ring-2 focus-within:ring-inset  hover:bg-gray-50"
									>
										<div className="flex justify-between space-x-3">
											<div className=" min-w-0 flex-1">
												<Link
													to={`/${procedure.administration.country.code}/${procedure.administration.slug}/${procedure.slug}`}
													preventScrollReset
													prefetch="intent"
													className="block text-xl font-extrabold text-amber-700 no-underline focus:outline-none "
												>
													{procedure.name}
												</Link>
											</div>
										</div>

										<Link
											to={`/${procedure.administration.country.code}/${procedure.administration.slug}`}
											prefetch="intent"
										>
											<Badge
												className="rounded-sm text-gray-600"
												variant="outline"
											>
												{procedure.administration.country.name} ·{' '}
												{procedure.administration.name}
											</Badge>{' '}
										</Link>

										{/* <div className="pt-4 text-sm text-gray-500">
											{procedure.administration.country.name} ·{' '}
											{procedure.administration.name}
										</div> */}
										<p
											className="line-clamp-2 text-sm text-gray-600"
											dangerouslySetInnerHTML={{ __html: procedure.content }}
										></p>
									</li>
								))}
							</ul>
							<div className="mt-8">
								<PaginationBar 
									currentPage={data.page || 1}
									totalPages={Math.ceil(data.totalCount / data.pageSize)}
									linkTo={(page) => `?q=${encodeURIComponent(data.q)}&page=${page}`}
								/>
							</div>
						</>
					) : (
						<p>No se han encontrado resultados</p>
					)
				) : data.status === 'error' ? (
					<ErrorList errors={['There was an error parsing the results']} />
				) : null}
			</main>
		</div>
	)
}

export const meta: MetaFunction<typeof loader> = () => {
	return [
		{
			property: 'robots',
			content: 'index, follow',
		},
		{ title: 'Buscar trámites' },
		{
			name: 'description',
			content: 'Buscar',
		},
		{tagName: 'link', rel: 'canonical', href: `https://papelea.com/buscar`},
		{property: 'og:title', content: 'Buscar'},
		{
			property: 'og:type',
			content: 'website',
		},
		{
			property: 'og:url',
			content: `https://papelea.com/buscar`,
		},
		{
			property: 'og:description',
			content: 'Buscar',
		},
	]
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
